import { ArrowRightIcon, DollarSignIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { OnboardingClearEnrollmentsModal } from 'components/member_onboarding_v2/components/OnboardingClearEnrollmentsModal'
import {
  FormFormattedNumberFieldDS,
  FormRadioGroupDS,
} from 'components/reusable/form/FormFields'
import { Banner, Button, ButtonGroup, useBreakpoint } from 'slab'
import { getNumericValueFromNumberOrString } from 'utils'

import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'
import { hasExistingOnThatchEnrollments } from '../utils'

type AdditionalDetailsProps = {
  tobacco: string;
  household_income: string | number | null;
}

const convertToDollars = (cents: number | undefined | null): number => {
  if (cents === undefined || cents === null) return 0
  return cents / 100
}

export function AdditionalDetailsPage ({
  navigateToNextStep,
  navigateToPreviousStep,
}: StepComponentProps) {
  const { onboardingData, updateMemberInfo, prefillDummyData } = useOnboarding()
  const [submitError, setSubmitError] = useState<React.ReactElement | null>(
    null
  )
  const [showModal, setShowModal] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<AdditionalDetailsProps | null>(null)

  const { breakpointIsAtLeast } = useBreakpoint()

  const getDummyData = (field: keyof AdditionalDetailsProps) => {
    if (!prefillDummyData) {
      return null
    }

    return {
      tobacco: 'false',
      household_income: '100000',
    }[field]
  }

  const getDefaultValues = (): AdditionalDetailsProps => ({
    tobacco: onboardingData?.member?.tobacco ? 'true' : 'false',
    household_income: onboardingData?.member?.household_income?.subunits
      ? convertToDollars(onboardingData.member.household_income.subunits)
      : getDummyData('household_income'),
  })

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid, dirtyFields },
  } = useForm<AdditionalDetailsProps>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  })

  // This hook is needed in case the data refreshes.
  useEffect(() => {
    reset(getDefaultValues())
  }, [onboardingData])

  const maybeSubmit = (formData: AdditionalDetailsProps) => {
    const existingEnrollments = hasExistingOnThatchEnrollments(onboardingData)
    if (existingEnrollments && dirtyFields.tobacco) {
      setPendingFormData(formData)
      setShowModal(true)
    } else {
      submitForm(formData)
    }
  }

  const submitForm = (formData: AdditionalDetailsProps) => {
    if (!formData.household_income) return
    const householdIncomeNumber = getNumericValueFromNumberOrString(formData.household_income)

    updateMemberInfo.mutate(
      {
        member: {
          tobacco: formData.tobacco === 'true',
          household_income: householdIncomeNumber,
        },
      },
      {
        onSuccess: (updatedData) => {
          setSubmitError(null)
          navigateToNextStep(updatedData)
        },
        onError: (error) => {
          if (error.status === 422) {
            const fieldErrors = error.fieldErrors!
            Object.entries(fieldErrors).forEach(([field, errorMessages]) => {
              setError(field as keyof AdditionalDetailsProps, {
                type: 'manual',
                message: errorMessages.join(', '),
              })
            })
          } else {
            setSubmitError(<OnboardingGenericErrorMessage error={error} />)
          }
        },
      }
    )
  }

  function renderButtons () {
    const backButton = (
      <Button
        variant='secondary'
        onClick={() => navigateToPreviousStep?.()}
        disabled={updateMemberInfo.isPending}
      >
        Back
      </Button>
    )

    const nextButton = (
      <Button
        rightIcon={ArrowRightIcon}
        variant='primary'
        type='submit'
        disabled={updateMemberInfo.isPending || !isValid}
      >
        See available plans
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup size='large'>
          {backButton}
          {nextButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {nextButton}
        {backButton}
      </ButtonGroup>
    )
  }

  return (
    <div data-testid='AdditionalDetailsPage' className='max-w-[44rem]'>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-3'>
          Provide additional details
        </h1>
        <p className='body text-gray-500'>
          A few more details to find the right plans for you.
        </p>
      </div>
      <form onSubmit={handleSubmit(maybeSubmit)}>
        <div className='flex flex-col gap-y-6 mb-10'>
          <FormFormattedNumberFieldDS<AdditionalDetailsProps>
            error={errors.household_income}
            fieldId='household_income'
            fieldLabel='Annual household income'
            description="Check if you're eligible for income-based incentives"
            register={register}
            placeholder='0.00'
            leadingIcon={DollarSignIcon}
            validationRule={{
              required: 'Household income is required.',
              validate: (value: string | number) => {
                const numericValue = getNumericValueFromNumberOrString(value)
                if (numericValue >= 100000000) {
                  return 'Household income cannot be $100,000,000 or more'
                }
                return true
              },
            }}
          />
          <div>
            <FormRadioGroupDS
              control={control}
              name='tobacco'
              label='Tobacco usage'
              options={[
                {
                  label: 'Never, or less than four times per week',
                  value: 'false',
                },
                {
                  label: 'Four or more times a week',
                  value: 'true',
                },
              ]}
              disabled={updateMemberInfo.isPending}
            />
          </div>
        </div>
        {submitError && (
          <div className='mb-10'>
            <Banner
              title='A problem has occurred while processing your request'
              type='critical'
            >
              {submitError}
            </Banner>
          </div>
        )}
        {renderButtons()}
      </form>
      <OnboardingClearEnrollmentsModal
        isModalOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setPendingFormData(null)
        }}
        onSubmit={() => {
          if (pendingFormData) {
            submitForm(pendingFormData!)
          } else {
            // this should never happen
            setError('root', {
              type: 'manual',
              message: 'Something went wrong submitting this form. Please try again.',
            })
          }
        }}
        description='Tobacco usage can impact plan cost and availability, so making a change here will remove any plans currently in your cart'
      />
    </div>
  )
}
